/**
 * The free tools: what each one measures, what it links to, and where it
 * lives.
 *
 * ── Why ten tools and not one ────────────────────────────────────────────────
 *
 * The site already had two — a composite and a face-shape detector — and they
 * share an engine, so the marginal cost of the other eight is copy rather than
 * code. What that buys is search surface: "canthal tilt", "fwhr", "gonial
 * angle" and "facial thirds" are queries people type, and a single page called
 * "free looksmax test" cannot rank for any of them. It also buys the thing the
 * GEO research actually rewards (docs/SEO-GEO-RESEARCH.md, finding 1): a page
 * whose H1 IS the question and whose first paragraph IS the answer is the shape
 * that gets quoted.
 *
 * Each tool is a URL, not a tab. A tab would collapse ten queries into one page
 * with one title, which is the single most common way a good set of tools
 * ranks for nothing.
 *
 * ── The related-tool graph ───────────────────────────────────────────────────
 *
 * `related` is hand-written per tool rather than derived, and that is
 * deliberate: an automatic "here are three other tools" block is a footer, and
 * a reader treats it as one. These pairs follow how the measurements actually
 * relate — canthal tilt sits next to eye spacing because both describe the eye
 * area, jawline next to face shape because the jaw is what decides the shape —
 * so the link is a next step rather than a nav dump. See `pseo-linking`.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import { en } from "./en";
import type { Tool, ToolCopy, ToolSlug } from "./types";
import { TOOL_SLUGS } from "./types";

export const TOOLS: readonly Tool[] = [
  {
    slug: "looksmax-test",
    metric: "composite",
    unit: "score",
    related: ["attractiveness-test", "face-shape", "face-symmetry"],
  },
  /**
   * ── The attractiveness test, and why it is the same number ────────────────
   *
   * Added 2026-10-09 as the one new page of that day's search work, and chosen
   * by demand rather than by what was easy to build. Autocomplete was measured
   * in all five languages: "attractiveness test", "how attractive am i" and
   * "rate my face" each return a full list in English, and so do
   * "yakışıklılık testi", "güzellik testi" and "yüz puanlama" in Turkish, "test
   * de atractivo" in Spanish and "teste de beleza" in Portuguese — the widest
   * demand of any query this site does not already answer. The pages ranking
   * for it put a sign-in in front of the result, cap it at one a day, or say
   * nothing about where the photo goes. "No account, nothing uploaded, the
   * arithmetic shown" is a better answer to that query than to any other.
   *
   * It is NOT a new score, and it must not become one. The glossary says of
   * PSL that "no tool can compute" it, and the same is true of attractiveness:
   * it is a judgment, and a tool that claimed to compute it would be the black
   * box this site is built against. What a photo CAN show is the geometry —
   * the eight measurements of the composite, with symmetry, the measurement
   * most consistently tied to how faces are rated, weighted heaviest. So this
   * page reads that composite on the scale the query asks in (out of 10),
   * sorts the eight into what helps and what holds it back, and says plainly
   * what a photo cannot see. A visitor who runs both this and the looksmax
   * test gets the same number twice, at 64 / 100 and 6.4 / 10, and the copy on
   * both pages says why.
   *
   * Its own URL rather than a tab on the looksmax test, for the reason in the
   * header above: "attractiveness test" and "looksmax test" are different
   * queries asked by different people — the second is community jargon, the
   * first is what everybody else types — and one page cannot be the answer to
   * both.
   */
  {
    slug: "attractiveness-test",
    metric: "attractiveness",
    unit: "ten",
    related: ["looksmax-test", "face-symmetry", "golden-ratio"],
  },
  {
    slug: "face-shape",
    metric: "faceShape",
    unit: "shape",
    related: ["jawline", "facial-thirds", "looksmax-test"],
  },
  {
    slug: "face-symmetry",
    metric: "symmetry",
    unit: "percent",
    related: ["looksmax-test", "facial-thirds", "eye-spacing"],
  },
  {
    slug: "golden-ratio",
    metric: "goldenRatio",
    unit: "ratio",
    related: ["facial-thirds", "fwhr", "lip-ratio"],
  },
  {
    slug: "canthal-tilt",
    metric: "canthalTilt",
    unit: "degrees",
    related: ["eye-spacing", "looksmax-test", "face-symmetry"],
  },
  {
    slug: "jawline",
    metric: "jawline",
    unit: "ratio",
    related: ["face-shape", "fwhr", "looksmax-test"],
  },
  {
    slug: "fwhr",
    metric: "fwhr",
    unit: "ratio",
    related: ["jawline", "face-shape", "golden-ratio"],
  },
  {
    slug: "facial-thirds",
    metric: "thirds",
    unit: "percent",
    related: ["golden-ratio", "face-shape", "looksmax-test"],
  },
  {
    slug: "eye-spacing",
    metric: "eyeSpacing",
    unit: "ratio",
    related: ["canthal-tilt", "face-symmetry", "golden-ratio"],
  },
  {
    slug: "lip-ratio",
    metric: "lipRatio",
    unit: "ratio",
    related: ["golden-ratio", "facial-thirds", "looksmax-test"],
  },
] as const;

const BY_SLUG = new Map(TOOLS.map((tool) => [tool.slug, tool]));

export function toolBySlug(slug: string): Tool | null {
  return BY_SLUG.get(slug as ToolSlug) ?? null;
}

/**
 * The path a tool lives at, in a language.
 *
 * `/tools/<slug>` for English and `/<lang>/tools/<slug>` for the rest — the
 * same rule the landing page follows (`lib/marketing/registry.ts`): English is
 * the bare path because it is what every existing inbound link points at, and
 * a second address for it would split that authority for nothing.
 *
 * The slug itself does NOT translate. A Turkish visitor searching for canthal
 * tilt types "canthal tilt" — the term is a loanword in every one of our five
 * languages, and a translated slug would be a URL nobody types and no existing
 * link points at.
 */
export function toolPath(slug: ToolSlug, lang: ContentLang = "en"): string {
  return lang === "en" ? `/tools/${slug}` : `/${lang}/tools/${slug}`;
}

/** The index of all tools, in a language. */
export function toolsIndexPath(lang: ContentLang = "en"): string {
  return lang === "en" ? "/tools" : `/${lang}/tools`;
}

/**
 * Load one language's tool copy.
 *
 * Dynamic per language for the same reason as the marketing bundles: five
 * copies of ten tools is a lot of text, and a Turkish visitor should not
 * download the Spanish one. English is the static import because it is both the
 * default and the fallback, so it is on the critical path either way.
 */
const LOADERS: Record<ContentLang, () => Promise<Record<ToolSlug, ToolCopy>>> =
  {
    en: async () => en,
    de: () => import("./de").then((m) => m.de),
    tr: () => import("./tr").then((m) => m.tr),
    pt: () => import("./pt").then((m) => m.pt),
    es: () => import("./es").then((m) => m.es),
  };

export function loadToolCopy(
  lang: ContentLang,
): Promise<Record<ToolSlug, ToolCopy>> {
  return LOADERS[lang]();
}

export type { Tool, ToolCopy, ToolSlug };
export { TOOL_SLUGS };
